import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Modal, ListGroup, Badge } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { ArticlesDataService } from '../../Services/ArticlesDataService';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { formatDate } from '../../Utils/formaters';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import Table from '../../Components/Table/Table';

const AdminNewsPageContent = () => {
    const readOnly = useReadOnly(Permissions.Collaboration.News);
    const { instance, accounts, inProgress } = useMsal();
    const [articles, setArticles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState({
        title: '',
        subtitle: '',
        content: '',
        isActive: true,
        coverImage: null, // To hold the uploaded file
        position: 0, // New field for article position
        startDate: '', // New field for start date
        endDate: '',   // New field for end date
    });

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchArticles();
        }
    }, [accounts, instance, inProgress]);

    const fetchArticles = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await ArticlesDataService.getAllArticles(response.accessToken); // Fetch all articles
            setArticles(data);
        } catch (error) {
            console.error('Failed to fetch articles:', error);
        }
    };

    const handleOpenModal = (article) => {
        setIsEditing(!!article);
        setSelectedArticle(article ? {
            ...article,
            startDate: formatDate(article.startDate), // Format the start date
            endDate: formatDate(article.endDate),     // Format the end date
        } : {
            title: '',
            subtitle: '',
            content: '',
            isActive: true,
            coverImage: null,
            position: 0,
            startDate: '',
            endDate: ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedArticle(null);
    };

    const handleSaveArticle = async () => {
        if (readOnly) { return; }
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const formData = new FormData();
            formData.append('Id', selectedArticle.id ?? 0);
            formData.append('Title', selectedArticle.title);
            formData.append('Subtitle', selectedArticle.subtitle);
            formData.append('Content', selectedArticle.content);
            formData.append('IsActive', selectedArticle.isActive);
            formData.append('Position', selectedArticle.position);
            formData.append('StartDate', selectedArticle.startDate ?? '');
            formData.append('EndDate', selectedArticle.endDate ?? '');
            
            if (selectedArticle.coverImage) {
                formData.append('CoverImage', selectedArticle.coverImage);
            }

            if (isEditing) {
                await ArticlesDataService.updateArticle(selectedArticle.id, formData, response.accessToken);
            } else {
                await ArticlesDataService.createArticle(formData, response.accessToken);
            }

            fetchArticles(); // Refresh the articles list
            handleCloseModal();
        } catch (error) {
            console.error('Failed to save article:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSelectedArticle((prevState) => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        setSelectedArticle((prevState) => ({
            ...prevState,
            coverImage: e.target.files[0] // Store the file object
        }));
    };

    const handleToggleActivation = async (id, isActive) => {
        if (readOnly) return;
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            await ArticlesDataService.toggleArticleActivation(id, isActive, response.accessToken);
            fetchArticles();
            setShowModal(false);
        } catch (error) {
            console.error('Failed to toggle activation:', error);
        }
    };

    const handleDelete = (article) => {
        setSelectedArticle(article)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirmation = async (id) => {
        if (readOnly) return;
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            let result = await ArticlesDataService.deleteArticle(id, response.accessToken);
            if(result && result.status == 200){
                fetchArticles();
                setShowDeleteModal(false);
            }
        } catch (error) {
            console.error('Failed to toggle activation:', error);
        }
    }

    const columns = [
        { header: 'Position', accessorKey: 'position' },
        { header: 'Title', accessorKey: 'title' },
        { header: 'Subtitle', accessorKey: 'subtitle' },
        { header: 'Start Date', accessorKey: 'startDate', cell: info => formatDate(info.getValue()) },
        { header: 'End Date', accessorKey: 'endDate', cell: info => formatDate(info.getValue()) },
        {
            header: 'Status',
            accessorKey: 'isActive',
            cell: ({ row }) => (
                <Badge pill className={row.original.isActive ? 'badge-success' : 'badge-danger'}>
                    {row.original.isActive ? 'Active' : 'Inactive'}
                </Badge>
            )
        },
        { header: 'Read Count', accessorKey: 'readCount' },
        {
            header: 'Actions',
            cell: ({ row }) => (
                <div className="d-flex justify-content-between">
                    <Button
                        className="me-2"
                        size="sm"
                        variant="info"
                        onClick={() => handleOpenModal(row.original)}>
                        Edit
                    </Button>
                    <Button disabled={readOnly}
                        className="me-2"
                        size="sm"
                        variant={row.original.isActive ? 'outline-danger' : 'outline-success'}
                        onClick={() => handleToggleActivation(row.original.id, !row.original.isActive)}>
                        {row.original.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="danger" onClick={() => handleDelete(row.original)}>
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Container fluid>
            <Row>
                <Col>
                    <Button disabled={readOnly} onClick={() => handleOpenModal(null)}>Create New Article</Button>
                    <div className="table-container mt-4">
                        <Table columns={columns} data={articles} tableClass="table-sm text-center" containerClass="table-responsive" headerClass="table-light" />
                    </div>
                </Col>
            </Row>
            <Modal size="lg" show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Article' : 'Create Article'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className='mb-2'>
                            <Form.Label>Title</Form.Label>
                            <Form.Control disabled={readOnly} type="text" name="title" value={selectedArticle?.title || ''} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className='mb-2'>
                            <Form.Label>Subtitle</Form.Label>
                            <Form.Control disabled={readOnly} type="text" name="subtitle" value={selectedArticle?.subtitle || ''} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className='mb-2'>
                            <Form.Label>Content</Form.Label>
                            <Form.Control disabled={readOnly} as="textarea" rows={3} name="content" value={selectedArticle?.content || ''} onChange={handleChange} required />
                        </Form.Group>
                        <Row className='mb-2'>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Position</Form.Label>
                                    <Form.Control disabled={readOnly} type="number" name="position" value={selectedArticle?.position} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control disabled={readOnly} type="date" name="startDate" value={selectedArticle?.startDate ?? ''} onChange={handleChange} required />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control disabled={readOnly} type="date" name="endDate" value={selectedArticle?.endDate ?? ''} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        {selectedArticle ? (
                            <>
                                <div className='mb-2'>
                                <Form.Label>Current Cover Image</Form.Label>
                                <ListGroup>
                                    { selectedArticle.coverImageUri ? (
                                        <ListGroup.Item>
                                        <a href={selectedArticle.coverImageUri} target="_blank" rel="noopener noreferrer">
                                            {selectedArticle.coverImageUri.split('/').pop().split('?')[0]}
                                        </a>
                                    </ListGroup.Item>
                                    ) : (
                                        <ListGroup.Item>No files attached.</ListGroup.Item>
                                    )}

                                </ListGroup>
                                </div>
                                <Form.Group className='mb-2'>
                                    <Form.Label>New Cover Image</Form.Label>
                                    <Form.Control disabled={readOnly} type="file" name="coverImage" onChange={handleFileChange} />
                                </Form.Group>
                            </>
                            
                        ) : (
                                <Form.Group className='mb-2'>
                                    <Form.Label>Cover Image</Form.Label>
                                    <Form.Control disabled={readOnly} type="file" name="coverImage" onChange={handleFileChange} />
                                </Form.Group>
                        )}
                        <Form.Group className='mb-2'>
                            <Form.Check disabled={readOnly}
                                type="checkbox"
                                label="Active"
                                name="isActive"
                                checked={selectedArticle?.isActive}
                                onChange={handleChange}
                            />
                        </Form.Group>
                        <div className='d-flex justify-content-end'>
                        <Button disabled={readOnly} variant="primary" onClick={handleSaveArticle}>
                            {isEditing ? 'Update Article' : 'Create Article'}
                        </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onAcceptClick={() => handleDeleteConfirmation(selectedArticle?.id)}
                onCancelClick={() => setShowDeleteModal(false)}
                title='Confirm action'
                content="Are you sure that you want to delete this article? This action can't be reversed."
            />
        </Container>
    );
};

const AdminNewsPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="News Manager">
                <AdminNewsPageContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default AdminNewsPage;
